import React, { useEffect, useMemo, useRef } from "react";
import { Rect, Transformer, Group, Text } from "react-konva";
import {
  getOriginalRectangleCoordinates,
  getRectangleCoordinates,
} from "../../helpers/coordinateParsers";
import { useDispatch, useSelector } from "react-redux";
import classes from "./new-rectangle.module.scss";
import {
  selectCanvasHeight,
  selectCanvasWidth,
  selectCurrentPageOriginalHeight,
  selectCurrentPageOriginalRotationAngle,
  selectCurrentPageOriginalWidth,
} from "../../reducers/pdf-viewer/selectors";
import {
  selectActiveDetectedFieldId,
  selectActiveObjectId,
  selectSettingColors,
} from "../../reducers/editor/selectors";
import { OBJECT_SHAPE_TYPES, OBJECT_TYPES } from "../../constants/project";
import { has, isNil, pathOr } from "ramda";
import {
  setActiveDetectedField,
  setActiveObjectId,
} from "../../reducers/editor/actions";
import {
  changeProjectObjectCoordinates,
  changeProjectObjectSizes,
} from "../../reducers/project/actions";
import { useStrictMode } from "react-konva";
import useImage from "use-image";
import { getClientRect } from "../../helpers/rectangle";

const NewRectangle = ({
  handleOnGroupItemClick,
  cursorIsOnCanvasItem = () => {},
  handleOnDoubleClick = () => {},
  setHoveringRectObject = () => {},
  color,
  object,
  canDraggable = true,
}) => {
  // To fix the group not render at the right position after dragging
  useStrictMode(true);

  const dispatch = useDispatch();
  const shapeRef = useRef();
  const recRef = useRef();
  const trRef = useRef();
  const textRef = useRef();

  const activeObjectId = useSelector(selectActiveObjectId);
  const activeDetectedFieldId = useSelector(selectActiveDetectedFieldId);
  const settingColors = useSelector(selectSettingColors);

  const canvasMarginLeft = 0;
  const canvasWidth = useSelector(selectCanvasWidth);
  const canvasHeight = useSelector(selectCanvasHeight);
  const currentPageOriginalHeight = useSelector(
    selectCurrentPageOriginalHeight
  );
  const currentPageOriginalWidth = useSelector(selectCurrentPageOriginalWidth);
  const currentPageOriginalRotationAngle = useSelector(
    selectCurrentPageOriginalRotationAngle
  );

  // For Preview feature
  // const [image] = useImage(pathOr(null, ["data", "url"], object));
  // const [checkedImage] = useImage("/img/check-mark.png");

  const isSelected = useMemo(() => {
    if (object.data.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      return object.data.id === activeDetectedFieldId;
    } else {
      return object.data.objectId === activeObjectId;
    }
  }, [activeDetectedFieldId, activeObjectId, object]);

  const coordinates = useMemo(() => {
    return getRectangleCoordinates(
      object,
      canvasWidth,
      canvasHeight,
      currentPageOriginalHeight,
      currentPageOriginalWidth,
      canvasMarginLeft,
      currentPageOriginalRotationAngle
    );
  }, [
    object,
    canvasWidth,
    canvasHeight,
    currentPageOriginalHeight,
    currentPageOriginalWidth,
    canvasMarginLeft,
    currentPageOriginalRotationAngle,
  ]);

  const currentContentShape = useMemo(() => {
    const isNewRectangle = isNil(pathOr(null, ["data", "objectId"], object));

    const objectType = isNewRectangle
      ? null
      : pathOr(null, ["data", "objectType"], object);

    switch (objectType) {
      case OBJECT_TYPES.ANNOTATION: {
        return OBJECT_SHAPE_TYPES.TEXT;
      }
      case OBJECT_TYPES.IMAGE: {
        return OBJECT_SHAPE_TYPES.IMAGE;
      }
      case OBJECT_TYPES.DETECTED_FIELD: {
        return OBJECT_SHAPE_TYPES.DETECTED_FIELD;
        // For Preview feature
        // const fieldType = pathOr("EditBox", ["data", "type"], object);
        // if (
        //   fieldType === "CheckBox" &&
        //   pathOr(null, ["data", "text"], object) === "True"
        // ) {
        //   return OBJECT_SHAPE_TYPES.CHECKED_IMAGE;
        // }
        //
        // if (
        //   fieldType === "CheckBox" &&
        //   pathOr(null, ["data", "text"], object) === "False"
        // ) {
        //   return OBJECT_SHAPE_TYPES.NO_CHECKED_IMAGE;
        // }
        //
        // return OBJECT_SHAPE_TYPES.TEXT;
      }

      default:
        return OBJECT_SHAPE_TYPES.NO_DATA;
    }
  }, [object]);

  const isRectangle = useMemo(() => {
    return (
      object.fieldProperties.rectangle[2] && object.fieldProperties.rectangle[3]
    );
  }, [object]);

  const shapeProps = useMemo(() => {
    const textProperties = {
      stroke: color,
      strokeWidth: 1,
      x: coordinates.topLeftX,
      y: coordinates.topLeftY,
      width: coordinates.width,
      height: coordinates.height,
      id: object.data.objectId,
      ellipsis: true,
      padding: 3,
    };

    switch (currentContentShape) {
      case OBJECT_SHAPE_TYPES.TEXT: {
        textProperties.text = pathOr("", ["data", "text"], object);
        textProperties.align = pathOr("left", ["data", "alignment"], object);
        break;
      }

      case OBJECT_SHAPE_TYPES.IMAGE: {
        textProperties.text = `< Image > \n ${object.data.url}`;
        break;
      }

      case OBJECT_SHAPE_TYPES.DETECTED_FIELD: {
        textProperties.text = `< PDF Field > \n\n ${object.data.text}`;
        break;
      }
    }

    return textProperties;
  }, [
    color,
    coordinates.topLeftX,
    coordinates.topLeftY,
    coordinates.width,
    coordinates.height,
    object,
    currentContentShape,
    isRectangle,
  ]);

  // For Preview feature
  const previewShapeProps = useMemo(() => {
    switch (currentContentShape) {
      case OBJECT_SHAPE_TYPES.TEXT: {
        return {
          stroke: pathOr("#333333", ["data", "color"], object),
          strokeWidth: 1,
          text: object.data.text,
          fontSize: object.data.size,
          fontFamily: object.data.fontName,
          align: object.data.alignment,
          fontStyle: object.data.fontItalic
            ? "italic"
            : object.data.fontBold
            ? "bold"
            : "normal",
          textDecoration: object.data.fontUnderline ? "underline" : "",
          x: coordinates.topLeftX,
          y: coordinates.topLeftY,
          width: coordinates.width,
          height: coordinates.height,
          id: object.data.objectId,
          verticalAlign:
            object.data.objectType === OBJECT_TYPES.DETECTED_FIELD
              ? "middle"
              : "top",
          padding: 2,
        };
      }

      case OBJECT_SHAPE_TYPES.IMAGE: {
        return {
          stroke: color,
          strokeWidth: 1,
          keepRatio: object.data.keepAspectRatio,
          x: coordinates.topLeftX,
          y: coordinates.topLeftY,
          width: coordinates.width,
          height: coordinates.height,
          id: object.data.objectId,
        };
      }

      case OBJECT_SHAPE_TYPES.CHECKED_IMAGE: {
        return {
          stroke: color,
          strokeWidth: 1,
          x: coordinates.topLeftX,
          y: coordinates.topLeftY,
          width: coordinates.width,
          height: coordinates.height,
          id: object.data.objectId,
        };
      }

      case OBJECT_SHAPE_TYPES.NO_CHECKED_IMAGE: {
        return {
          stroke: color,
          strokeWidth: 1,
          x: coordinates.topLeftX,
          y: coordinates.topLeftY,
          width: coordinates.width,
          height: coordinates.height,
          id: object.data.objectId,
        };
      }
    }
  }, [coordinates, currentContentShape, color]);

  const rectShapeProps = useMemo(() => {
    const data = {
      stroke: color,
      x: coordinates.topLeftX,
      y: coordinates.topLeftY,
      width: coordinates.width,
      height: coordinates.height,
      id: object.data.objectId,
    };

    if (object.data.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      if (!has("objectId", object.data)) {
        data.fill = settingColors.detectedField;
        data.opacity = 0.3;
      }
    }

    return data;
  }, [coordinates, color, object]);

  const handleOnClickItem = () => {
    handleOnGroupItemClick(false);
    const type = pathOr(null, ["data", "objectType"], object);
    if (type === OBJECT_TYPES.DETECTED_FIELD) {
      dispatch(
        setActiveDetectedField(
          object.objectId ? object.objectId : object.data.id
        )
      );
    } else {
      dispatch(setActiveObjectId(object.objectId));
    }
  };

  const handleOnMouseDown = (e) => {
    setHoveringRectObject(shapeProps);
    cursorIsOnCanvasItem(e);
    const container = e.target.getStage().container();
    container.style.cursor = "grabbing";
  };

  const handleDragEnd = () => {
    const node = shapeRef.current;
    let deltaX = node.x();
    let deltaY = node.y();

    const { topLeftX, topLeftY } = getOriginalRectangleCoordinates(
      object,
      currentPageOriginalWidth,
      currentPageOriginalHeight,
      currentPageOriginalRotationAngle
    );

    let coefficientX;
    let coefficientY;
    if (
      currentPageOriginalRotationAngle === 270 ||
      currentPageOriginalRotationAngle === 90
    ) {
      coefficientX = currentPageOriginalWidth / canvasHeight;
      coefficientY = currentPageOriginalHeight / canvasWidth;
    } else {
      coefficientX = currentPageOriginalWidth / canvasWidth;
      coefficientY = currentPageOriginalHeight / canvasHeight;
    }

    deltaX *= coefficientX;
    deltaY *= coefficientY;

    changeObjectCoordinates(deltaX + topLeftX, deltaY + topLeftY);
    dispatch(setActiveObjectId(object.objectId));
    handleOnGroupItemClick(true);
  };

  const handleTransform = () => {
    const node = recRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    let top = node.y();
    let left = node.x();
    let width = node.width() * scaleX;
    let height = node.height() * scaleY;

    let coefficientX;
    let coefficientY;
    if (
      currentPageOriginalRotationAngle === 270 ||
      currentPageOriginalRotationAngle === 90
    ) {
      coefficientX = currentPageOriginalWidth / canvasHeight;
      coefficientY = currentPageOriginalHeight / canvasWidth;
    } else {
      coefficientX = currentPageOriginalWidth / canvasWidth;
      coefficientY = currentPageOriginalHeight / canvasHeight;
    }

    top = Math.round(top) * coefficientY;
    left = (Math.round(left) - canvasMarginLeft) * coefficientX;
    width = Math.round(width) * coefficientX;
    height = Math.round(height) * coefficientY;

    changeObjectCoordinates(left, top);
    changeObjectSizes(width, height);

    node.scaleX(1);
    node.scaleY(1);
    handleOnGroupItemClick(true);
  };

  const handleDragMove = () => {
    setHoveringRectObject(null);
    const box = shapeRef.current.getClientRect();
    const absPos = shapeRef.current.getAbsolutePosition();
    const offsetX = box.x - absPos.x;
    const offsetY = box.y - absPos.y;

    const newAbsPos = { ...absPos };
    if (box.x < 0) {
      newAbsPos.x = -offsetX;
    }
    if (box.y < 0) {
      newAbsPos.y = -offsetY;
    }
    if (box.x + box.width > canvasWidth) {
      newAbsPos.x = canvasWidth - box.width - offsetX;
    }
    if (box.y + box.height > canvasHeight) {
      newAbsPos.y = canvasHeight - box.height - offsetY;
    }

    if (isRectangle) {
      shapeRef.current.setAbsolutePosition(newAbsPos);
    }
  };

  const changeObjectCoordinates = (x, y) => {
    return dispatch(changeProjectObjectCoordinates(object.objectId, x, y));
  };

  const changeObjectSizes = (w, h) => {
    return dispatch(changeProjectObjectSizes(object.objectId, w, h));
  };

  const boundBoxFunc = (oldBox, newBox) => {
    const box = getClientRect(newBox);
    const isOut =
      box.x < 0 ||
      box.y < 0 ||
      box.x + box.width > canvasWidth ||
      box.y + box.height > canvasHeight;

    // if new bounding box is out of visible viewport, let's just skip transforming
    // this logic can be improved by still allow some transforming if we have small available space
    if (isOut) {
      return oldBox;
    }

    return newBox;
  };

  const freeHeightBaseOnObjectType = useMemo(() => {
    const isNewRectangle = isNil(pathOr(null, ["data", "objectId"], object));
    const objectType = isNewRectangle
      ? null
      : pathOr(null, ["data", "objectType"], object);

    return objectType === OBJECT_TYPES.IMAGE ? 15 : 5;
  }, [object]);

  useEffect(() => {
    if (isRectangle) return;

    recRef.current.setAttrs({
      width: pathOr(null, ["current", "textWidth"], textRef)
        ? pathOr(null, ["current", "textWidth"], textRef) + 20
        : null,
      height: pathOr(null, ["current", "textWidth"], textRef)
        ? pathOr(null, ["current", "textHeight"], textRef) *
            pathOr(null, ["current", "textArr", "length"], textRef) +
          freeHeightBaseOnObjectType
        : null,
    });
  }, [textRef.current, recRef.current, isRectangle, coordinates, object]);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      // we need to attach transformer manually
      trRef.current.nodes([shapeRef.current]);
      if (recRef.current) {
        trRef.current.nodes([recRef.current]);
      }
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      {isRectangle ? (
        <>
          <Group
            draggable={canDraggable}
            onClick={handleOnClickItem}
            onDblClick={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "crosshair";
              handleOnDoubleClick(e, object);
            }}
            onMouseDown={handleOnMouseDown}
            ref={shapeRef}
            x={0}
            y={0}
            onDragEnd={handleDragEnd}
            onDragMove={handleDragMove}
            onDragStart={() => dispatch(setActiveObjectId(object.objectId))}
            onMouseOver={(e) => {
              setHoveringRectObject(shapeProps);
            }}
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "grab";

              e.target.getStage().batchDraw();
            }}
            onMouseUp={(e) => {
              setHoveringRectObject(null);
              const container = e.target.getStage().container();
              container.style.cursor = "grab";
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "crosshair";
            }}
            onMouseOut={(e) => {
              setHoveringRectObject(null);
              e.target.getStage().batchDraw();
            }}
          >
            <Rect
              ref={recRef}
              {...rectShapeProps}
              stroke={
                isSelected ? settingColors.drawingObject : shapeProps.stroke
              }
            />

            <Text
              ref={textRef}
              {...shapeProps}
              stroke={
                isSelected ? settingColors.drawingObject : shapeProps.stroke
              }
            />
            {/*For preview feature*/}

            {/*{currentContentShape === OBJECT_SHAPE_TYPES.IMAGE && (*/}
            {/*  <Image {...shapeProps} image={image} />*/}
            {/*)}*/}

            {/*{currentContentShape === OBJECT_SHAPE_TYPES.CHECKED_IMAGE && (*/}
            {/*  <Image {...shapeProps} image={checkedImage} />*/}
            {/*)}*/}
          </Group>

          {isSelected && (
            <Transformer
              anchorStroke={settingColors.drawingObject}
              anchorFill={settingColors.drawingObject}
              borderStroke={settingColors.drawingObject}
              anchorSize={6}
              onTransformEnd={handleTransform}
              rotateEnabled={false}
              ref={trRef}
              keepRatio={false}
              // enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
              resizeEnabled={
                pathOr(false, ["data", "objectType"], object) !==
                OBJECT_TYPES.DETECTED_FIELD
              }
              boundBoxFunc={boundBoxFunc}
            />
          )}
        </>
      ) : (
        <>
          <Group
            draggable={canDraggable}
            onClick={handleOnClickItem}
            onDblClick={(e) => {
              if (textRef.current) {
                const container = e.target.getStage().container();
                container.style.cursor = "crosshair";
                handleOnDoubleClick(e, object);
              }
            }}
            onMouseDown={handleOnMouseDown}
            ref={shapeRef}
            x={0}
            y={0}
            width={null}
            height={null}
            onDragEnd={handleDragEnd}
            onDragMove={handleDragMove}
            onDragStart={() => dispatch(setActiveObjectId(object.objectId))}
            onMouseOver={(e) => setHoveringRectObject(shapeProps)}
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "grab";
            }}
            onMouseUp={(e) => {
              setHoveringRectObject(null);
              const container = e.target.getStage().container();
              container.style.cursor = "grab";
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "crosshair";
            }}
            onMouseOut={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "crosshair";
              setHoveringRectObject(null);
            }}
          >
            <Rect
              ref={recRef}
              {...rectShapeProps}
              width={
                pathOr(null, ["current", "textWidth"], textRef)
                  ? pathOr(null, ["current", "textWidth"], textRef) + 20
                  : null
              }
              height={
                pathOr(null, ["current", "textWidth"], textRef)
                  ? pathOr(null, ["current", "textHeight"], textRef) *
                      pathOr(null, ["current", "textArr", "length"], textRef) +
                    freeHeightBaseOnObjectType
                  : null
              }
              stroke={
                isSelected ? settingColors.drawingObject : shapeProps.stroke
              }
            />

            <Text
              ref={textRef}
              {...shapeProps}
              width={null}
              height={null}
              ellipsis={true}
              wrap="none"
              stroke={
                isSelected ? settingColors.drawingObject : shapeProps.stroke
              }
            />
          </Group>
        </>
      )}
    </>
  );
};

export default NewRectangle;
